import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  palettes,
  genPageTheme,
  shapes,
} from '@backstage/theme';

const NAVY = '#0A2540';
const NAVY_LIGHT = '#1a4a7a';
const NAVY_DEEP = '#061829';
const ORANGE = '#F97316';

const boazLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: NAVY, light: NAVY_LIGHT, dark: NAVY_DEEP, contrastText: '#FFFFFF' },
    secondary: { main: ORANGE, light: '#FDBA74', dark: '#C2410C', contrastText: '#FFFFFF' },
    navigation: {
      background: NAVY,
      indicator: ORANGE,
      color: '#b5c8e8',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: 'rgba(249, 115, 22, 0.1)' },
      submenu: { background: NAVY_DEEP },
    },
    background: { default: '#F0F4F8', paper: '#FFFFFF' },
    error: { main: '#e53935' },
    warning: { main: '#f59e0b' },
    info: { main: '#2563eb' },
    success: { main: '#16a34a' },
  },
  fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  htmlFontSize: 16,
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: [NAVY, NAVY_LIGHT], shape: shapes.wave }),
    documentation: genPageTheme({ colors: [NAVY_LIGHT, '#2d6aa0'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: [NAVY, NAVY_LIGHT], shape: shapes.round }),
    service: genPageTheme({ colors: [NAVY, NAVY_LIGHT], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#4bb8a9', NAVY_LIGHT], shape: shapes.wave }),
    library: genPageTheme({ colors: [NAVY, NAVY_LIGHT], shape: shapes.wave2 }),
    other: genPageTheme({ colors: [NAVY, '#2d6aa0'], shape: shapes.circle }),
    app: genPageTheme({ colors: [NAVY, NAVY_LIGHT], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#2d6aa0', NAVY], shape: shapes.wave }),
  },
});

const boazDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: '#4a9eff', light: '#82c0ff', dark: '#1a6ccc', contrastText: '#FFFFFF' },
    secondary: { main: ORANGE, light: '#FDBA74', dark: '#C2410C', contrastText: '#FFFFFF' },
    navigation: {
      background: '#0d1b2e',
      indicator: ORANGE,
      color: '#90afd4',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: 'rgba(249, 115, 22, 0.15)' },
      submenu: { background: '#060f1a' },
    },
    background: { default: '#0f1923', paper: '#162032' },
    error: { main: '#f44336' },
    warning: { main: '#ffa726' },
    info: { main: '#42a5f5' },
    success: { main: '#66bb6a' },
  },
  fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  htmlFontSize: 16,
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#0d1b2e', '#1a3556'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#1a3556', '#1e4a80'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#0d1b2e', '#1a3556'], shape: shapes.round }),
    service: genPageTheme({ colors: ['#0d1b2e', '#1a3556'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#1a3556', '#2a5990'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#0d1b2e', '#1a3556'], shape: shapes.wave2 }),
    other: genPageTheme({ colors: ['#0d1b2e', '#1a3556'], shape: shapes.circle }),
    app: genPageTheme({ colors: ['#0d1b2e', '#1a3556'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#1a3556', '#0d1b2e'], shape: shapes.wave }),
  },
});
import {
  GrafanaIcon,
  KeycloakIcon,
  ArgoCDIcon,
  KibanaIcon,
  SonarQubeIcon,
  MinIOIcon,
  PgAdminIcon,
} from './components/icons/ToolIcons';

const app = createApp({
  apis,
  icons: {
    grafana: GrafanaIcon,
    keycloak: KeycloakIcon,
    argocd: ArgoCDIcon,
    kibana: KibanaIcon,
    sonarqube: SonarQubeIcon,
    minio: MinIOIcon,
    pgadmin: PgAdminIcon,
  },
  themes: [
    {
      id: 'boaz-light',
      title: 'Clair',
      variant: 'light' as const,
      Provider: ({ children }: { children: React.ReactNode }) => (
        <UnifiedThemeProvider theme={boazLightTheme}>{children}</UnifiedThemeProvider>
      ),
    },
    {
      id: 'boaz-dark',
      title: 'Sombre',
      variant: 'dark' as const,
      Provider: ({ children }: { children: React.ReactNode }) => (
        <UnifiedThemeProvider theme={boazDarkTheme}>{children}</UnifiedThemeProvider>
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'github',
            title: 'GitHub',
            message: 'Connectez-vous avec votre compte GitHub Boaz Study.',
            apiRef: githubAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
